// Batch 28 · Follow-up 1 · F2 — the two draft hooks behind every Save / Cancel bar.
//
// useFormDraft(saved): a page form keeps what the user typed in a draft; nothing is written while typing. ``patch`` holds only
// the fields that differ from the saved record; Save sends it once; Cancel (``reset``) puts back the saved values.
//
// useTableDraft({ rows, getId, saveRow }): a table keeps per-row drafts. ``changedIds`` are the rows whose values differ from
// the saved ones (the bar shows the count and the rows are highlighted); Save sends ONLY those rows, each with its own
// ``saveRow(id, patch, row)`` call (the existing per-row PATCH), one after the other — untouched rows are never written; then
// ``reload`` once (the saved values replace the drafts without a flash of the old ones). A
// row whose call fails keeps its draft and its error (``errorOf``) and the others still save; Save again retries it.
import { useCallback, useMemo, useRef, useState } from "react";
import { toErrMsg } from "@/lib/api";

const blank = (v) => v === undefined || v === null || v === "";
export function sameValue(a, b) {
  if (blank(a) && blank(b)) return true;
  if (typeof a === "number" || typeof b === "number") return Number(a) === Number(b) && !blank(a) && !blank(b);
  if (typeof a === "object" || typeof b === "object") return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return a === b;
}

export function useFormDraft(saved) {
  const [draft, setDraft] = useState({});
  const base = useMemo(() => saved || {}, [saved]);   // Step 7c · E3: one {} while saved is null (was a new one per render)
  const value = useMemo(() => ({ ...base, ...draft }), [base, draft]);
  const changed = Object.keys(draft).filter((k) => !sameValue(draft[k], base[k]));
  const patch = {};
  changed.forEach((k) => { patch[k] = draft[k]; });
  const set = useCallback((k, v) => setDraft((d) => ({ ...d, [k]: v })), []);
  const setMany = useCallback((obj) => setDraft((d) => ({ ...d, ...obj })), []);
  const reset = useCallback(() => setDraft({}), []);
  return { value, draft, set, setMany, changed, patch, dirty: changed.length > 0, reset, cancel: reset };
}

// Step 11c · C3c: ``update(id, field, fn)`` sets a field from its CURRENT draft (or saved) value — two edits made before the
// screen re-renders both count (find 26: two fast assertion ticks kept only the last). ``saveRows(list)`` (optional, find 19):
// Save sends every changed row in ONE call — list = [{id, patch, row}], resolves {saved: [ids], failed: {id: message}} —
// instead of one ``saveRow`` call per row.
export function useTableDraft({ rows, getId = (r) => r.id, saveRow, saveRows, reload }) {
  const [edits, setEdits] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const byId = useMemo(() => {
    const m = {};
    (Array.isArray(rows) ? rows : []).forEach((r) => { m[String(getId(r))] = r; });
    return m;
  }, [rows, getId]);
  const patchOf = useCallback((id) => {
    const e = edits[String(id)] || {};
    const row = byId[String(id)] || {};
    const p = {};
    Object.keys(e).forEach((k) => { if (!sameValue(e[k], row[k])) p[k] = e[k]; });
    return p;
  }, [edits, byId]);
  // a draft of a row no longer in the table (deleted, moved to the other tab) does not count and is never sent
  const changedIds = Object.keys(edits).filter((id) => byId[id] && Object.keys(patchOf(id)).length > 0);
  const failedIds = changedIds.filter((id) => errors[id]);
  const saveRef = useRef(saveRow);
  saveRef.current = saveRow;
  const saveRowsRef = useRef(saveRows);
  saveRowsRef.current = saveRows;
  const byIdRef = useRef(byId);
  byIdRef.current = byId;
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const edit = useCallback((id, field, v) => {
    const k = String(id);
    setEdits((all) => ({ ...all, [k]: { ...(all[k] || {}), [field]: v } }));
  }, []);
  const update = useCallback((id, field, fn) => {
    const k = String(id);
    setEdits((all) => {
      const cur = all[k] && field in all[k] ? all[k][field] : (byIdRef.current[k] || {})[field];
      return { ...all, [k]: { ...(all[k] || {}), [field]: fn(cur) } };
    });
  }, []);
  const rowValue = useCallback((row) => ({ ...row, ...(edits[String(getId(row))] || {}) }), [edits, getId]);
  const cancel = useCallback(() => { setEdits({}); setErrors({}); }, []);
  /** Saves the changed rows one by one. Resolves { saved: [ids], failed: [ids] }. */
  const save = useCallback(async () => {
    const ids = Object.keys(edits).filter((id) => byId[id] && Object.keys(patchOf(id)).length > 0);
    const ok = [];
    const bad = {};
    setSaving(true);
    try {
      if (saveRowsRef.current && ids.length) {
        try {
          const r = await saveRowsRef.current(ids.map((id) => ({ id, patch: patchOf(id), row: byId[id] })));
          const failed = (r && r.failed) || {};
          ids.forEach((id) => { if (failed[id]) bad[id] = failed[id]; else ok.push(id); });
        } catch (e) { ids.forEach((id) => { bad[id] = toErrMsg(e, "Not saved"); }); }
      } else {
        for (const id of ids) {
          try { await saveRef.current(id, patchOf(id), byId[id]); ok.push(id); }
          catch (e) { bad[id] = toErrMsg(e, "Not saved"); }
        }
      }
      if (ok.length && reloadRef.current) { try { await reloadRef.current(); } catch (e) { /* the screen shows its own error */ } }
    } finally { setSaving(false); }
    setEdits((all) => { const n = { ...all }; ok.forEach((id) => { delete n[id]; }); return n; });
    setErrors(bad);
    return { saved: ok, failed: Object.keys(bad) };
  }, [edits, patchOf, byId]);
  /** STEP 11d · A9: saves ONE row (its draft only; the other rows keep theirs), then ``reload``. Resolves {ok, saved, error}:
   *  ok false = the save failed (the row keeps its draft and shows the error). A row with no change resolves {ok: true}. */
  const saveOne = useCallback(async (id) => {
    const k = String(id);
    const p = patchOf(k);
    if (!byId[k] || !Object.keys(p).length) return { ok: true, saved: false, error: "" };
    setSaving(true);
    try {
      if (saveRowsRef.current) {
        const r = await saveRowsRef.current([{ id: k, patch: p, row: byId[k] }]);
        const f = r && r.failed && r.failed[k];
        if (f) throw new Error(typeof f === "string" ? f : "Not saved");
      } else {
        await saveRef.current(k, p, byId[k]);
      }
    } catch (e) {
      const msg = toErrMsg(e, "Not saved");
      setErrors((all) => ({ ...all, [k]: msg }));
      setSaving(false);
      return { ok: false, saved: false, error: msg };
    }
    setEdits((all) => { const n = { ...all }; delete n[k]; return n; });
    setErrors((all) => { const n = { ...all }; delete n[k]; return n; });
    if (reloadRef.current) { try { await reloadRef.current(); } catch (e) { /* the screen shows its own error */ } }
    setSaving(false);
    return { ok: true, saved: true, error: "" };
  }, [patchOf, byId]);
  return {
    edit, update, rowValue, cancel, save, saveOne, saving, changedIds, failedIds, dirty: changedIds.length > 0,
    isChanged: (id) => changedIds.includes(String(id)), errorOf: (id) => errors[String(id)] || "", patchOf,
    rowClass: (id) => (errors[String(id)] ? "bg-red-50 dark:bg-red-950/30 outline outline-1 outline-red-300"
      : changedIds.includes(String(id)) ? "bg-amber-50 dark:bg-amber-950/30" : ""),
  };
}
